// The release manager: an unattended agent (scripts/release-manager.mjs) that
// reads every feedback item, plans a release, hands the work to the triage
// agent as feedback items, reviews what comes back, and tags the result for
// prod once Sam has checked it on dev. These are its records.

export const RELEASE_STATUS = {
  PLANNED: "planned", // plan written, nothing handed to the triage agent yet
  BUILDING: "building", // items queued/in flight
  ON_DEV: "on-dev", // every item merged to main (= live on dev), waiting on Sam
  APPROVED: "approved", // Sam checked it on dev and said go — admin login only
  TAGGED: "tagged", // version tag pushed; Sam's own prod deploy takes it from here
  CANCELLED: "cancelled",
} as const;
export type ReleaseStatus = (typeof RELEASE_STATUS)[keyof typeof RELEASE_STATUS];

// Allowed status moves — enforced server-side so neither a buggy agent nor a
// stale admin panel can skip a step (e.g. tag something Sam never approved).
export const RELEASE_TRANSITIONS: Record<ReleaseStatus, ReleaseStatus[]> = {
  planned: ["building", "cancelled"],
  building: ["on-dev", "planned", "cancelled"],
  "on-dev": ["approved", "building", "cancelled"],
  approved: ["tagged", "on-dev", "cancelled"],
  tagged: [],
  cancelled: [],
};

export type ReleaseData = {
  type: "release";
  id: string;
  version: string; // proposed tag, e.g. "v0.4.0" — must match ^v\d+\.\d+\.\d+$ to be tagged
  title: string;
  status: ReleaseStatus;
  plan: string; // markdown
  // Anything in this release that needs a serverless.yml / infra change, in
  // plain words, so Sam can do it (or schedule it) deliberately. Empty/absent
  // = none.
  infraNotes?: string;
  // Customer-facing release notes (markdown). Admin endpoints only; editable
  // until the release is tagged, then frozen.
  publicNotes?: string;
  itemIds: string[]; // feedback item ids in scope
  releaseSha?: string; // the commit to tag: the last release item's merge commit on main
  approvedAt?: string;
  tag?: string;
  taggedAt?: string;
  createdAt: string;
  updatedAt: string;
};

export const MESSAGE_FROM = {
  SAM: "sam",
  MANAGER: "manager",
} as const;
export type MessageFrom = (typeof MESSAGE_FROM)[keyof typeof MESSAGE_FROM];

export const GENERAL_THREAD = "general";

export type ManagerMessageData = {
  type: "message";
  id: string;
  threadId: string; // "general", or a release id
  from: MessageFrom; // set server-side from how the caller authenticated
  text: string;
  createdAt: string;
};

// One per thread. `summary` is the rolling digest of everything older than
// `summarizedThrough`, so a long conversation costs a bounded prompt: the
// manager reads the summary plus only the messages after it, and folds the
// old ones in as the thread grows.
export type ManagerThreadData = {
  type: "thread";
  threadId: string;
  summary?: string;
  summarizedThrough?: string; // createdAt of the newest message folded into `summary`
  lastHandledAt?: string; // the manager has read/replied to everything up to here
  lastSeenBySamAt?: string; // drives the "new from manager" badge in the admin panel
  updatedAt: string;
};

export const MESSAGE_TEXT_MAX = 4000;
export const THREAD_SUMMARY_MAX = 6000;
export const RELEASE_PLAN_MAX = 20000;
export const RELEASE_NOTES_MAX = 4000;
